import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
 

document.addEventListener('DOMContentLoaded', function() {
  
    // --- 1. Main Function to Update Prices ---
    function updatePrices() {
        // Find the currently selected input within the Kaching Bundle block
        const input = document.querySelector('.kaching-bundles__bar input:checked');
        
        // Safety check: if no bundle is selected, exit early
        if (!input) return;

        // --- SOURCE DATA (Get prices from the selected bundle) ---
        const bundleRow = input.closest('.kaching-bundles__bar');
        if (!bundleRow) return;

        const sourcePriceEl = bundleRow.querySelector('.kaching-bundles__bar-price');
        const sourceCompareEl = bundleRow.querySelector('.kaching-bundles__bar-full-price');
        
        // Ensure we handle cases where innerHTML might be empty using optional chaining
        const newPriceHtml = sourcePriceEl?.innerHTML?.trim() || '';
        const oldPriceHtml = sourceCompareEl?.innerHTML?.trim() || '';

        // If no primary price found, exit
        if (!newPriceHtml) return;

        // VARIABLE TO STORE SUBSCRIPTION PRICE (so we can pass it to Target 4)
        let finalSubscriptionPriceHtml = '';

        // --- TARGET 1: MAIN PRODUCT PRICE CONTAINER ---
        const mainContainer = document.querySelector('.product-meta__price-list-container');
        if (mainContainer) {
            const mainPrice = mainContainer.querySelector('.price.price--highlight') || mainContainer.querySelector('.price');
            const mainCompare = mainContainer.querySelector('.price.price--compare');

            if (mainPrice) mainPrice.innerHTML = newPriceHtml;
            
            if (mainCompare) {
                if (oldPriceHtml) {
                    mainCompare.innerHTML = oldPriceHtml;
                    mainCompare.style.display = '';
                } else {
                    mainCompare.style.display = 'none';
                }
            }
            
            // Dispatch events so other apps detect the change
            mainContainer.dispatchEvent(new Event('change', { bubbles: true }));
        }


        // --- TARGET 2: OTP (One-Time Purchase) WIDGET ---
        const otpContent = document.querySelector('#stay-control-otp + .stay-controls__control-content');
        if (otpContent) {
            const otpSale = otpContent.querySelector('.stay-controls__price-sale');
            const otpRegular = otpContent.querySelector('.stay-controls__price-regular');

            if (otpSale) otpSale.innerHTML = newPriceHtml;
            if (otpRegular) {
                if (oldPriceHtml) {
                    otpRegular.innerHTML = oldPriceHtml;
                    otpRegular.style.display = '';
                } else {
                    otpRegular.style.display = 'none';
                }
            }
        }


        // --- TARGET 3: SUB (Subscription) WIDGET ---
        const subContent = document.querySelector('#stay-control-sub + .stay-controls__control-content');
        if (subContent) {
            const subRegular = subContent.querySelector('.stay-controls__price-regular');
            const subSale = subContent.querySelector('.stay-controls__price-sale');
            const percentSpan = subContent.querySelector('span'); // Usually holds "Subscribe & Save X%"

            // Update the baseline price for the subscription block
            if (subRegular) {
                subRegular.innerHTML = newPriceHtml;
                subRegular.style.display = ''; 
            }

            // Calculate the actual subscription sale price based on the percentage
            if (subSale && percentSpan) {
                const text = percentSpan.innerText || "";
                const match = text.match(/(\d+)%/); // Extract the digit before the '%'

                if (match && match[1]) {
                    const subPercent = parseFloat(match[1]);
                    // Clean symbols to do math
                    const numericPrice = parseFloat(newPriceHtml.replace(/[^0-9.]/g, ''));

                    if (!isNaN(numericPrice)) {
                        const finalSubPrice = numericPrice * (1 - (subPercent / 100));
                        const currencySymbol = newPriceHtml.replace(/[\d.,\s]/g, '').replace(/&nbsp;/ig, '').trim();
                        
                        // STORE the final calculated subscription HTML
                        finalSubscriptionPriceHtml = currencySymbol + finalSubPrice.toFixed(2);
                        
                        subSale.innerHTML = finalSubscriptionPriceHtml;
                    }
                }
            }
        }


        // --- TARGET 4: PRODUCT-PRICE WEB COMPONENT ---
        const productPriceComponent = document.querySelector('product-price');
        if (productPriceComponent) {
            const componentInnerPrice = productPriceComponent.querySelector('.price');
            const componentComparePrice = productPriceComponent.querySelector('.compare-at-price');
            const componentDiscountBadge = productPriceComponent.querySelector('.price--total-discount');
            const componentDiscountSpan = componentDiscountBadge ? componentDiscountBadge.querySelector('span') : null;

            // 1. Update Current Active Price
            if (componentInnerPrice) componentInnerPrice.innerHTML = newPriceHtml;

            // 2. Logic for Old Price and DISCOUNT PERCENTAGE logic
            if (oldPriceHtml) {
                if (componentComparePrice) {
                    componentComparePrice.innerHTML = oldPriceHtml;
                    componentComparePrice.style.display = '';
                }

                // Extract pure numbers
                const newPriceNum = parseFloat(newPriceHtml.replace(/[^0-9.]/g, ''));
                const oldPriceNum = parseFloat(oldPriceHtml.replace(/[^0-9.]/g, ''));

                if (!isNaN(newPriceNum) && !isNaN(oldPriceNum) && oldPriceNum > newPriceNum) {
                    // Formula: ((Old - New) / Old) * 100
                    const discountPercent = Math.round(((oldPriceNum - newPriceNum) / oldPriceNum) * 100);
                    
                    if (componentDiscountSpan) {
                        componentDiscountSpan.innerText = `${discountPercent}% Off`;
                    } else if (componentDiscountBadge) {
                        componentDiscountBadge.innerText = `${discountPercent}% Off`;
                    }
                    
                    if (componentDiscountBadge) componentDiscountBadge.style.display = ''; 
                } else {
                    if (componentDiscountBadge) componentDiscountBadge.style.display = 'none';
                }

            } else {
                if (componentComparePrice) componentComparePrice.style.display = 'none';
                if (componentDiscountBadge) componentDiscountBadge.style.display = 'none';
            }
            
            // 3. CREATE OR UPDATE THE .pricesubscribe ELEMENT
            // Try to find the exact container via [ref="priceContainer"], or fallback to the whole component itself
            //  const priceContainerRef = productPriceComponent.querySelector('[ref="priceContainer"]') || productPriceComponent;
            const priceContainerRef = productPriceComponent.querySelector('[ref="priceContainer-test"]');
            
            if (priceContainerRef) {
                // Check if we already injected our div previously
                let subscribeDiv = priceContainerRef.querySelector('.pricesubscribe');
                
                // If the subscription calculation (from Target 3) yielded a valid string
                if (finalSubscriptionPriceHtml !== '') {
                    
                    // If it doesn't exist in the DOM, create and append it
                    if (!subscribeDiv) {
                        subscribeDiv = document.createElement('div');
                        subscribeDiv.className = 'pricesubscribe';
                        priceContainerRef.appendChild(subscribeDiv);
                    }
                    
                    // Update content and make sure it is visible
                    subscribeDiv.innerHTML = `<span class="pricesubscribe__text">Subscribe & Save</span> `+finalSubscriptionPriceHtml;
                    subscribeDiv.style.display = '';
                } else {
                    // If there is no subscription price available (or it failed), hide the injected block safely
                    if (subscribeDiv) subscribeDiv.style.display = 'none';
                }
            }

            productPriceComponent.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // --- TARGET 5: STICKY ADD TO CART PRICE ---
        // const stickyBarPrice = document.querySelector('.product-sticky-form__price');
        // if (stickyBarPrice && newPriceHtml) {
        //     stickyBarPrice.innerHTML = newPriceHtml;
        // }
         const stickyBarPrice = document.querySelectorAll('.product-sticky-form__price');
        if (stickyBarPrice.length > 0 && newPriceHtml) {
          stickyBarPrice.forEach(price => {
            price.innerHTML = newPriceHtml;
          });
        }
    }


    // --- 2. INITIAL PAGE LOAD CALCULATION ---
    
    // Run immediately in case HTML is already rendered server-side
    updatePrices();
    
    // Safety fallback timeouts: Wait for Kaching or Stay apps to finish loading their dynamic HTML
    setTimeout(() => {
        updatePrices();
    }, 500);

    setTimeout(() => {
        updatePrices();
    }, 1500);


    // --- 3. Event Listeners with Safety Checks ---
    document.addEventListener('change', function(event) {
        if (!event || !event.target) return;

        const target = event.target;

        // A. Kaching Bundle selection listener
        if (target.closest('.kaching-bundles__bar input')) {
            updatePrices();
        }

        // B. Variant Picker listener (Radio buttons)
        const variantPicker = target.closest('variant-picker');
        if (variantPicker && target.type === 'radio') {
            // Delay update to allow Shopify/Kaching to finish DOM manipulation
            setTimeout(() => {
                updatePrices();
            }, 2000);
        }
    });


    // --- 4. MutationObserver for Dynamic Changes ---
    const targetNode = document.querySelector('.kaching-bundles-block');

    if (targetNode) {
        // Run once as soon as observer is set
        updatePrices();
        
        const config = { childList: true, subtree: true };
        const observer = new MutationObserver(function() {
            updatePrices();
        });
        observer.observe(targetNode, config);
    } else {
        // Fallback for cases where Kaching block is lazy-loaded by Shopify
        const bodyObserver = new MutationObserver((mutations, obs) => {
            const node = document.querySelector('.kaching-bundles-block');
            if (node) {
                updatePrices();
                new MutationObserver(() => updatePrices()).observe(node, { childList: true, subtree: true });
                obs.disconnect(); 
            }
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
    }
});